import { t } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ContentIFramePageLayout } from '@/layout/components/content-iframe-page-layout/ContentIframePageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { isProduction } from '@/utils/environment'
import { useHylyght } from '../hooks/useHylyght'
import { StIFrame } from './HylyghtInjected.styled'

const HylyghtInjectedPage = () => {
  // State
  // ------------------------------------------------------------------------- /
  const [hylyghtError, setHylyghtError] = useState<{
    title: string
    message: string
  }>()

  const [loading, setLoading] = useState(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [searchParameters] = useSearchParams()
  const navigate = useNavigate()
  const { getHylyghtActivityById } = useHylyght()

  // Hide navigation on native devices
  useHideNativeNavigation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const hylyghtUrl = searchParameters.get('hylyghtUrl')

  const activityId = searchParameters.get('activityId')
  const hylyghtId = searchParameters.get('hylyghtId')
  const hylyghtType = searchParameters.get('hylyghtType')

  const errorTitle = t({
    id: 'hylyghtInjectedPage.errorTitle',
    message: 'Er lijkt iets mis te zijn gegaan',
  })

  const errorMessage = t({
    id: 'hylyghtInjectedPage.errorMessage',
    message: 'Er is geen Hylyght URL opgegeven',
  })

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    const hylyghtUrl = searchParameters.get('hylyghtUrl')

    if (hylyghtUrl) {
      mixpanel.track('hylyght_injected_page_' + hylyghtUrl)
    } else {
      setHylyghtError({
        title: errorTitle,
        message: errorMessage,
      })
    }
  }, [searchParameters, errorTitle, errorMessage])

  // Methods
  // ------------------------------------------------------------------------- /

  const handleGoBack = async () => {
    setLoading(true)
    mixpanel.track('hylyght_injected_page_closed')
    try {
      await syncHylyghtActivity()
    } catch (error) {
      console.error(error)
    } finally {
      setTimeout(() => {
        navigate(`/task/${activityId}`)
        setLoading(false)
      }, 500)
    }
  }

  /**
   * Syncs the Hylyght activity for the current task and updates the task state
   * if the completedDates array has changed.
   *
   * This ensures the UI reflects the most up-to-date completion status for the activity.
   *
   * @returns {Promise<void>} Resolves when the sync is complete.
   */
  const syncHylyghtActivity = async () => {
    if (!hylyghtId) return

    try {
      await getHylyghtActivityById(hylyghtId, (hylyghtType as 'questionnaire' | 'program') ?? 'questionnaire')
    } catch (error) {
      console.error(error)
    }
  }

  // Render
  // ------------------------------------------------------------------------- /

  return (
    <ContentIFramePageLayout onGoBack={handleGoBack}>
      {loading ? (
        <Loader small />
      ) : hylyghtUrl && !hylyghtError ? (
        <StIFrame
          ref={iframeRef}
          key={activityId}
          src={hylyghtUrl}
          title='hylyght'
          onError={(error) => {
            setHylyghtError({
              title: errorTitle,
              message: errorMessage + ' ' + (isProduction() ? '' : error.toString()),
            })
          }}
        />
      ) : (
        <ErrorLayout title={hylyghtError?.title || errorTitle} message={hylyghtError?.message || errorMessage} />
      )}
    </ContentIFramePageLayout>
  )
}

export { HylyghtInjectedPage }
